import React, { useMemo, useState } from "react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ComposedChart,
  Legend,
  Line,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import {
  POINT_TYPES,
  POINT_TYPE_COLORS,
  formatBucket,
  formatCompactCurrency,
  formatCompactNumber,
  formatCurrency,
  formatDecimal,
  formatNumber,
} from "../metrics";
import { EmptyState } from "./Ui";

const AXIS = { fontSize: 11, fill: "#64748b" };
const GRID = "#eef2f7";

function ChartTooltip({ active, payload, label, granularity, valueFormatter }) {
  if (!active || !payload || !payload.length) return null;
  const rows = payload.filter((p) => p.value !== undefined && p.value !== null);
  return (
    <div className="bd-chart-tooltip">
      <div className="bd-chart-tooltip__title">{formatBucket(label, granularity)}</div>
      {rows.map((p) => (
        <div key={p.dataKey} className="bd-chart-tooltip__row">
          <i style={{ background: p.color || p.fill }} />
          <span>{p.name}</span>
          <strong>{valueFormatter ? valueFormatter(p.value, p.dataKey) : formatNumber(p.value)}</strong>
        </div>
      ))}
    </div>
  );
}

const TREND_MODES = [
  { id: "points", label: "Points by type" },
  { id: "meetings", label: "Meetings" },
  { id: "revenue", label: "Revenue" },
];

/**
 * Trend chart with a small mode switcher: stacked points by type, meetings
 * booked vs held, or closed-won revenue.
 */
export function TrendChart({ trend = [], granularity, height = 280, initialMode = "points", modes = TREND_MODES }) {
  const [mode, setMode] = useState(initialMode);
  const data = useMemo(
    () =>
      trend.map((b) => ({
        bucket: b.bucket,
        ...b.byType,
        points: b.points,
        pointValue: b.pointValue,
        meetingsBooked: b.meetingsBooked,
        meetingsHappened: b.meetingsHappened,
        tapsHeld: b.tapsHeld || 0,
        callsHeld: b.callsHeld || 0,
        revenue: b.revenue,
        wonDeals: b.wonDeals,
        bdDealsClosed: b.bdDealsClosed || 0,
        newClients: b.newClients || 0,
        curateDinners: b.curateDinners || 0,
      })),
    [trend]
  );
  const hasData = data.some((d) => d.points || d.meetingsBooked || d.meetingsHappened || d.revenue);
  const activeTypes = POINT_TYPES.filter((t) => data.some((d) => d[t.type]));

  return (
    <div className="bd-trend">
      <div className="bd-trend__modes">
        {modes.map((m) => (
          <button
            key={m.id}
            type="button"
            className={`bd-chip ${mode === m.id ? "is-active" : ""}`}
            onClick={() => setMode(m.id)}
          >
            {m.label}
          </button>
        ))}
      </div>
      {!hasData ? (
        <EmptyState title="No activity in this period" icon="~" />
      ) : (
        <ResponsiveContainer width="100%" height={height}>
          {mode === "revenue" ? (
            <ComposedChart data={data} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
              <CartesianGrid stroke={GRID} vertical={false} />
              <XAxis dataKey="bucket" tickFormatter={(v) => formatBucket(v, granularity)} tick={AXIS} axisLine={false} tickLine={false} />
              <YAxis yAxisId="left" tickFormatter={formatCompactCurrency} tick={AXIS} axisLine={false} tickLine={false} width={64} />
              <YAxis yAxisId="right" orientation="right" tick={AXIS} axisLine={false} tickLine={false} width={32} allowDecimals={false} />
              <Tooltip
                content={
                  <ChartTooltip
                    granularity={granularity}
                    valueFormatter={(v, k) => (k === "revenue" ? formatCurrency(v) : formatNumber(v))}
                  />
                }
              />
              <Legend iconType="circle" iconSize={8} wrapperStyle={{ fontSize: 12 }} />
              <Bar yAxisId="left" dataKey="revenue" name="Closed-won revenue" fill="#10b981" radius={[4, 4, 0, 0]} maxBarSize={48} />
              <Line yAxisId="right" dataKey="wonDeals" name="Closed-won deals" stroke="#0f172a" strokeWidth={2} dot={{ r: 3 }} />
              <Line yAxisId="right" dataKey="newClients" name="New clients" stroke="#0ea5e9" strokeWidth={2} dot={{ r: 3 }} strokeDasharray="4 3" />
            </ComposedChart>
          ) : mode === "meetings" ? (
            <ComposedChart data={data} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
              <CartesianGrid stroke={GRID} vertical={false} />
              <XAxis dataKey="bucket" tickFormatter={(v) => formatBucket(v, granularity)} tick={AXIS} axisLine={false} tickLine={false} />
              <YAxis tick={AXIS} axisLine={false} tickLine={false} width={36} allowDecimals={false} />
              <Tooltip content={<ChartTooltip granularity={granularity} />} />
              <Legend iconType="circle" iconSize={8} wrapperStyle={{ fontSize: 12 }} />
              <Bar dataKey="meetingsBooked" name="Meetings booked (WP points)" fill="#93c5fd" radius={[4, 4, 0, 0]} maxBarSize={48} />
              <Bar dataKey="tapsHeld" name="TAPs held (TAP call reports)" stackId="held" fill="#2563eb" maxBarSize={48} />
              <Bar dataKey="callsHeld" name="Other calls held (call reports)" stackId="held" fill="#8492a6" radius={[4, 4, 0, 0]} maxBarSize={48} />
              <Line dataKey="meetingsHappened" name="Meetings held (total)" stroke="#0f172a" strokeWidth={2} dot={{ r: 3 }} />
            </ComposedChart>
          ) : (
            <ComposedChart data={data} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
              <CartesianGrid stroke={GRID} vertical={false} />
              <XAxis dataKey="bucket" tickFormatter={(v) => formatBucket(v, granularity)} tick={AXIS} axisLine={false} tickLine={false} />
              <YAxis yAxisId="left" tick={AXIS} axisLine={false} tickLine={false} width={36} allowDecimals={false} />
              <YAxis yAxisId="right" orientation="right" tick={AXIS} axisLine={false} tickLine={false} width={40} tickFormatter={formatCompactNumber} />
              <Tooltip
                content={
                  <ChartTooltip
                    granularity={granularity}
                    valueFormatter={(v, k) => (k === "pointValue" ? formatDecimal(v) : formatNumber(v))}
                  />
                }
              />
              <Legend iconType="circle" iconSize={8} wrapperStyle={{ fontSize: 12 }} />
              {activeTypes.map((t) => (
                <Bar key={t.type} yAxisId="left" dataKey={t.type} name={t.label} stackId="pts" fill={t.color} maxBarSize={48} />
              ))}
              <Line yAxisId="right" dataKey="pointValue" name="Point value" stroke="#0f172a" strokeWidth={2} dot={{ r: 3 }} />
            </ComposedChart>
          )}
        </ResponsiveContainer>
      )}
    </div>
  );
}

/**
 * Donut of points by type with a legend listing count + value.
 */
export function BreakdownDonut({ byType = {}, height = 220 }) {
  const data = POINT_TYPES.map((t) => ({
    type: t.type,
    label: t.label,
    count: (byType[t.type] && byType[t.type].count) || 0,
    value: (byType[t.type] && byType[t.type].value) || 0,
    color: t.color,
  })).filter((d) => d.count > 0);
  const total = data.reduce((s, d) => s + d.count, 0);
  if (!total) return <EmptyState title="No points in this period" icon="◔" />;
  return (
    <div className="bd-donut">
      <div className="bd-donut__chart">
        <ResponsiveContainer width="100%" height={height}>
          <PieChart>
            <Pie data={data} dataKey="count" nameKey="label" innerRadius="62%" outerRadius="90%" paddingAngle={2} stroke="none">
              {data.map((d) => (
                <Cell key={d.type} fill={d.color} />
              ))}
            </Pie>
            <Tooltip formatter={(v, n) => [formatNumber(v), n]} contentStyle={{ fontSize: 12, borderRadius: 8 }} />
          </PieChart>
        </ResponsiveContainer>
        <div className="bd-donut__center">
          <strong>{formatNumber(total)}</strong>
          <span>points</span>
        </div>
      </div>
      <ul className="bd-donut__legend">
        {data.map((d) => (
          <li key={d.type}>
            <i style={{ background: d.color }} />
            <span className="bd-donut__legend-label">{d.label}</span>
            <span className="bd-donut__legend-count">{formatNumber(d.count)}</span>
            <span className="bd-donut__legend-value">{formatDecimal(d.value)} pts</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

/**
 * Horizontal comparison bars (e.g. team vs team). rows: [{ key, name, value, color, highlight }]
 */
export function HorizontalBars({ rows = [], format = (v) => formatNumber(v), onClick }) {
  const max = Math.max(1, ...rows.map((r) => Number(r.value) || 0));
  return (
    <ul className="bd-hbars">
      {rows.map((r) => (
        <li
          key={r.key}
          className={`bd-hbars__row ${r.highlight ? "is-highlight" : ""} ${onClick ? "is-clickable" : ""}`}
          onClick={onClick ? () => onClick(r) : undefined}
        >
          <span className="bd-hbars__label" title={r.sub || undefined}>
            {r.name}
          </span>
          <span className="bd-hbars__track">
            <span className="bd-hbars__fill" style={{ width: `${Math.round(((Number(r.value) || 0) / max) * 100)}%`, background: r.color }} />
          </span>
          <span className="bd-hbars__value">
            {format(r.value)}
            {r.sub && <small className="bd-hbars__sub">{r.sub}</small>}
          </span>
        </li>
      ))}
    </ul>
  );
}

/**
 * Tiny sparkline bar chart used inside cards.
 */
export function SparkBars({ trend = [], dataKey = "points", color = "#2563eb", height = 36 }) {
  const data = trend.map((b) => ({ bucket: b.bucket, v: b[dataKey] || 0 }));
  if (!data.some((d) => d.v)) return <div className="bd-spark bd-spark--empty" style={{ height }} />;
  return (
    <div className="bd-spark" style={{ height }}>
      <ResponsiveContainer width="100%" height={height}>
        <BarChart data={data} margin={{ top: 0, right: 0, left: 0, bottom: 0 }}>
          <Bar dataKey="v" fill={color} radius={[2, 2, 0, 0]} isAnimationActive={false} />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

export { POINT_TYPE_COLORS };
